import type {
  DrilldownFrozenSnapshot,
  DrilldownIntent,
  DrilldownIntentSeed,
  DrilldownSeed,
} from "./types"

function isDrilldownFrozenSnapshot(
  snapshot: Partial<DrilldownFrozenSnapshot> | undefined,
): snapshot is DrilldownFrozenSnapshot {
  return snapshot != null
    && typeof snapshot.modelId === "string"
    && "positionSet" in snapshot
    && "base" in snapshot
    && typeof snapshot.startDate === "string"
    && typeof snapshot.endDate === "string"
    && typeof snapshot.isIntradayMode === "boolean"
}

function mergeFrozenSnapshots(
  existingFrozenSnapshot: DrilldownFrozenSnapshot | undefined,
  intentFrozenSnapshot: Partial<DrilldownFrozenSnapshot> | undefined,
  clickedDate?: string,
): DrilldownFrozenSnapshot | undefined {
  let mergedFrozenSnapshot: DrilldownFrozenSnapshot | undefined

  if (existingFrozenSnapshot) {
    mergedFrozenSnapshot = intentFrozenSnapshot
      ? { ...existingFrozenSnapshot, ...intentFrozenSnapshot }
      : existingFrozenSnapshot
  } else if (isDrilldownFrozenSnapshot(intentFrozenSnapshot)) {
    mergedFrozenSnapshot = intentFrozenSnapshot
  } else {
    return undefined
  }

  if (clickedDate) {
    mergedFrozenSnapshot = { ...mergedFrozenSnapshot, endDate: clickedDate }
  }

  return mergedFrozenSnapshot
}

export function matchesDrilldownSeed(
  existingSeed: DrilldownSeed | undefined,
  intentSeed: DrilldownIntentSeed,
  sourcePanelId: string,
): boolean {
  if (!existingSeed) return false
  if (existingSeed.sourcePanelId !== sourcePanelId) return false
  if (existingSeed.view !== intentSeed.view) return false

  const intentIsCategoryOnly = intentSeed.selectedFactor == null && intentSeed.selectedCategory != null
  if (intentIsCategoryOnly && existingSeed.selectedFactor != null) {
    return false
  }

  if (existingSeed.selectedFactor != null || intentSeed.selectedFactor != null) {
    const resolvedFactor = intentSeed.selectedFactor ?? existingSeed.selectedFactor
    return existingSeed.selectedFactor === resolvedFactor
  }

  const resolvedCategory = intentSeed.selectedCategory ?? existingSeed.selectedCategory
  return existingSeed.selectedCategory === resolvedCategory
}

export function mergeDrilldownSeed(
  existingSeed: DrilldownSeed | undefined,
  intent: DrilldownIntent,
): DrilldownSeed {
  const { frozenSnapshot: intentFrozenSnapshot, ...intentSeedWithoutSnapshot } = intent.seed

  const mergedSeed: DrilldownSeed = {
    ...existingSeed,
    ...intentSeedWithoutSnapshot,
    sourcePanelId: intent.sourcePanelId,
  }

  const mergedFrozenSnapshot = mergeFrozenSnapshots(
    existingSeed?.frozenSnapshot,
    intentFrozenSnapshot,
    intent.seed.clickedDate,
  )

  if (mergedFrozenSnapshot) {
    mergedSeed.frozenSnapshot = mergedFrozenSnapshot
  }

  return mergedSeed
}
